import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Modal, In } from "@/pages/Workers";
import { Loader2, Plus, Edit2, Trash2, ShieldCheck, UserCog } from "lucide-react";

const ROLE_LABELS = { super_admin: "Super Admin", hr_manager: "HR Manager", employee: "Employee" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    const [u, m] = await Promise.all([api.get("/users"), api.get("/permissions/meta")]);
    setUsers(u.data); setMeta(m.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Delete login for ${u.name} (${u.email})?`)) return;
    await api.delete(`/users/${u.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Access Control</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Users & Access</h1>
          <p className="text-stone-600 mt-1 text-sm">Create logins and choose exactly which modules each user can see and edit.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-user-button"
                className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
          <Plus className="w-4 h-4" /> Add User
        </button>
      </header>

      {loading ? <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="card-flat p-0 overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-500 bg-stone-50/60">
              <th className="px-5 py-3">User</th><th>Role</th><th>Module Access</th><th>Status</th><th className="px-4"></th>
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-stone-100" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium flex items-center gap-1.5">
                      {u.role === "super_admin" && <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--color-primary)" }} />}
                      {u.name}{u.id === me?.id && <span className="text-[10px] text-stone-400">(you)</span>}
                    </div>
                    <div className="text-xs text-stone-500">{u.email}</div>
                  </td>
                  <td><span className="status-pill" style={{ background: "#F4F1DE", color: "#57534E" }}>{ROLE_LABELS[u.role] || u.role}</span></td>
                  <td className="max-w-[24rem]">
                    {u.role === "super_admin" ? (
                      <span className="text-xs font-semibold" style={{ color: "var(--color-primary)" }}>All modules</span>
                    ) : (
                      <div className="flex flex-wrap gap-1 py-1.5">
                        {(u.effective_modules || []).slice(0, 6).map((k) => (
                          <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">{labelFor(meta, k)}</span>
                        ))}
                        {(u.effective_modules || []).length > 6 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500">+{u.effective_modules.length - 6} more</span>
                        )}
                        {(u.effective_modules || []).length === 0 && <span className="text-xs text-stone-400">No access</span>}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="status-pill" data-testid={`user-status-${u.id}`}
                          style={u.is_active === false ? { background: "#FADED7", color: "#9A3412" } : { background: "#D8F3DC", color: "#1B4332" }}>
                      {u.is_active === false ? "Disabled" : "Active"}
                    </span>
                  </td>
                  <td className="px-4 whitespace-nowrap text-right">
                    <button onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`}
                            className="p-1.5 rounded hover:bg-stone-100"><Edit2 className="w-3.5 h-3.5" /></button>
                    {u.id !== me?.id && (
                      <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}
                              className="p-1.5 rounded text-stone-400 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && meta && <UserForm meta={meta} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && meta && <UserForm meta={meta} existing={editing} me={me} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function labelFor(meta, key) {
  return meta?.modules?.find((m) => m.key === key)?.label || key;
}

function UserForm({ meta, existing, me, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    name: existing?.name || "",
    email: existing?.email || "",
    password: "",
    role: existing?.role || "hr_manager",
    is_active: existing ? existing.is_active !== false : true,
  });
  const [modules, setModules] = useState(
    existing ? (existing.modules ?? existing.effective_modules ?? []) : (meta.templates["hr_manager"] || [])
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onRoleChange = (role) => {
    setForm({ ...form, role });
    // Pre-fill checkboxes from the role template (admin can still customise)
    setModules(meta.templates[role] || []);
  };

  const toggle = (key) => {
    setModules((m) => m.includes(key) ? m.filter((k) => k !== key) : [...m, key]);
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      if (isEdit) {
        const body = { name: form.name, role: form.role, modules, is_active: form.is_active };
        if (form.password) body.password = form.password;
        await api.put(`/users/${existing.id}`, body);
      } else {
        await api.post("/users", { name: form.name, email: form.email, password: form.password, role: form.role, modules });
      }
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Failed to save user");
    } finally { setBusy(false); }
  };

  const isSuperAdmin = form.role === "super_admin";

  return (
    <Modal title={isEdit ? `Edit User · ${existing.name}` : "Add User"} onClose={onClose} testid="user-modal">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <In label="Full Name" v={form.name} on={(v) => setForm({ ...form, name: v })} required />
          {isEdit ? (
            <label className="block"><div className="kpi-label mb-1.5">Email</div>
              <input value={form.email} disabled className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-stone-50 text-stone-500" />
            </label>
          ) : (
            <In label="Email (login)" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} required />
          )}
          <In label={isEdit ? "New Password (leave blank to keep)" : "Password (min 6 chars)"} type="password"
              v={form.password} on={(v) => setForm({ ...form, password: v })} required={!isEdit} />
          <label className="block">
            <div className="kpi-label mb-1.5">Role (sets default access)</div>
            <select value={form.role} onChange={(e) => onRoleChange(e.target.value)} data-testid="user-role-select"
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
              <option value="hr_manager">HR Manager</option>
              <option value="employee">Employee</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </label>
        </div>

        {isSuperAdmin ? (
          <div className="p-3 rounded-md text-xs flex items-center gap-2" style={{ background: "#D8F3DC", color: "#1B4332" }}>
            <ShieldCheck className="w-4 h-4 shrink-0" /> Super Admins always have full access to every module, including the Danger Zone and Users & Access.
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="kpi-label flex items-center gap-1.5"><UserCog className="w-3.5 h-3.5" /> Module Access · {modules.length} of {meta.modules.length}</div>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={() => setModules(meta.modules.map((m) => m.key))} data-testid="select-all-modules"
                        className="px-2 py-1 rounded border border-stone-200 hover:bg-stone-50">Select all</button>
                <button type="button" onClick={() => setModules([])} data-testid="clear-all-modules"
                        className="px-2 py-1 rounded border border-stone-200 hover:bg-stone-50">Clear</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto p-3 rounded-md border border-stone-200" data-testid="module-checkboxes">
              {meta.modules.map((m) => (
                <label key={m.key} className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer transition-colors ${modules.includes(m.key) ? "font-semibold" : "text-stone-600 hover:bg-stone-50"}`}
                       style={modules.includes(m.key) ? { background: "var(--color-primary-light, #EAF3EC)", color: "var(--color-primary)" } : {}}>
                  <input type="checkbox" checked={modules.includes(m.key)} onChange={() => toggle(m.key)}
                         data-testid={`module-checkbox-${m.key}`} className="accent-current" />
                  {m.label}
                </label>
              ))}
            </div>
          </div>
        )}

        {isEdit && existing.id !== me?.id && (
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                   data-testid="user-active-toggle" className="accent-current" />
            Account active (untick to disable login)
          </label>
        )}

        {error && <div className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-md" data-testid="user-form-error">{error}</div>}

        <button type="submit" disabled={busy} data-testid="save-user-button"
                className="w-full py-2 rounded-md text-white font-semibold" style={{ background: "var(--color-primary)" }}>
          {busy ? "Saving…" : isEdit ? "Save Changes" : "Create User"}
        </button>
      </form>
    </Modal>
  );
}
